import isEqual from 'lodash/isEqual';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { applySearchFilter } from '@/redux/modules/search';
import { EFilterMethod, setFormFilter } from '@/redux/modules/searchForm';
import { TRootState } from '@/redux/rootReducer';
import { lastMapPan, lastZoomDirection } from '@/redux/selectors/analytics-selectors';
import { getCurrency } from '@/redux/selectors/currency';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getMapBounds, getMapMarkers, IMapMarkerData, TBounds } from '@/redux/selectors/search/map';
import { getIsLoading } from '@/redux/selectors/search/searchResults';
import { trackEvent } from '@/services/track-event';

import SearchMap, { TViewportChangeBounds } from './SearchMap';

interface IProps {
  isDesktopMapViewExpanded: boolean;
  toggleExpandMapView: () => void;
  isOnPromoPage?: boolean;
  promoSlug?: string;
  allowPromoPageViewportChange?: boolean;
  isOnWishlist?: boolean;
  hasNavigation?: boolean;
  hasMapLayers?: boolean;
  customMarkers?: IMapMarkerData[];
  initialBounds?: TBounds | null;
  isOnListingPage?: boolean;
}

const SearchMapContainer = ({
  isDesktopMapViewExpanded,
  toggleExpandMapView,
  isOnPromoPage = false,
  promoSlug,
  allowPromoPageViewportChange = false,
  isOnWishlist = false,
  hasNavigation = true,
  hasMapLayers = true,
  customMarkers,
  initialBounds,
  isOnListingPage = false,
}: IProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsLoading);
  const hasResults = useSelector<TRootState, boolean>(state => !!state.search.data);
  const hasCampgroundResults = useSelector<TRootState, boolean>(
    state => !!state.search.campgrounds?.data,
  );
  const recalculateMapViewport = useSelector<TRootState, boolean>(
    state => state.search.recalculateMapViewport,
  );
  const currency = useSelector(getCurrency);
  const bounds = useSelector(getMapBounds);
  const query = useSelector(getQueryParams);
  const markers = useSelector(getMapMarkers);
  const searchMeta = useSelector<TRootState, TRootState['search']['meta']>(
    state => state.search.meta,
  );
  const handlerViewportChange = useCallback(
    (newBounds: TViewportChangeBounds, ignoreRecalculateMapViewport = false) => {
      if (isOnPromoPage && !allowPromoPageViewportChange) return;
      const zoomDir = lastZoomDirection();
      const mapPan = lastMapPan();
      const filterMethod =
        zoomDir != null
          ? zoomDir < 0
            ? EFilterMethod.MAP_ZOOM_OUT
            : EFilterMethod.MAP_ZOOM_IN
          : mapPan === 1
            ? EFilterMethod.MAP_PAN
            : null;
      dispatch(setFormFilter({ filters: newBounds, filterMethod }, true));
      dispatch(
        applySearchFilter(newBounds, ignoreRecalculateMapViewport, false, false, undefined, false),
      );
      if (!isEqual(query, newBounds)) {
        trackEvent({
          event: 'search/map',
          action: 'pan',
          before: query,
          after: newBounds,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, query],
  );

  if (!hasResults && !hasCampgroundResults && !isOnWishlist) {
    return null;
  }

  return (
    <SearchMap
      isLoading={isLoading}
      recalculateMapViewport={recalculateMapViewport}
      currency={currency}
      bounds={initialBounds || bounds}
      markers={customMarkers || markers}
      searchMeta={searchMeta}
      onViewportChange={handlerViewportChange}
      isDesktopMapViewExpanded={isDesktopMapViewExpanded}
      toggleExpandMapView={toggleExpandMapView}
      isOnPromoPage={isOnPromoPage}
      allowPromoPageViewportChange={allowPromoPageViewportChange}
      promoSlug={promoSlug}
      hasNavigation={hasNavigation}
      hasMapLayers={hasMapLayers}
      isOnWishlist={isOnWishlist}
      isOnListingPage={isOnListingPage}
    />
  );
};

export default SearchMapContainer;
